import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';

const CreateFOProductForm = ({ type, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        category: '',
        subcategory: [''],
        Brand: '',
        supplierCode: '',
        supplierName: '',
        supplierCategory: [],
        supplierSubcategory: [],
        supplierBrands: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [categoryNames, setCategoryNames] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [categoriesForSupplier, setCategoriesForSupplier] = useState([]);
    const [allSubcategoriesForSupplier, setAllSubcategoriesForSupplier] = useState([]);
    const [brandsForSupplier, setBrandsForSupplier] = useState([]);

    // Fetch available category names for autocomplete
    useEffect(() => {
        if (type === 'category') {
            const fetchCategoryNames = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_URL}/api/foproducts/categories/names`,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'ngrok-skip-browser-warning': 'true'
                            }
                        }
                    );
                    if (response.data.success) {
                        setCategoryNames(response.data.data);
                    }
                } catch (err) {
                    console.error('Failed to fetch category names:', err);
                }
            };
            fetchCategoryNames();
        }
    }, [type]);

    // Fetch subcategories when category changes
    useEffect(() => {
        if (type === 'category' && formData.category) {
            const fetchSubcategories = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_URL}/api/foproducts/categories/${formData.category}/subcategories`,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'ngrok-skip-browser-warning': 'true'
                            }
                        }
                    );
                    if (response.data.success) {
                        setSubcategories(response.data.data);
                    }
                } catch (err) {
                    console.error('Failed to fetch subcategories:', err);
                    setSubcategories([]);
                }
            };
            fetchSubcategories();
        } else {
            setSubcategories([]);
        }
    }, [type, formData.category]);

    // Fetch categories (full docs) and brands when type is supplier
    useEffect(() => {
        if (type === 'supplier') {
            const fetchOptions = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const [catRes, brandRes] = await Promise.all([
                        axios.get(`${process.env.REACT_APP_API_URL}/api/foproducts/categories?page=1&limit=500`, {
                            headers: { 'Authorization': `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
                        }),
                        axios.get(`${process.env.REACT_APP_API_URL}/api/foproducts/brands?page=1&limit=500`, {
                            headers: { 'Authorization': `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
                        })
                    ]);
                    if (catRes.data.success) setCategoriesForSupplier(catRes.data.data);
                    if (brandRes.data.success) setBrandsForSupplier(brandRes.data.data);
                    const allSub = (catRes.data.data || []).flatMap(c => c.subcategory || []).filter(Boolean);
                    setAllSubcategoriesForSupplier([...new Set(allSub)].sort());
                } catch (err) {
                    console.error('Failed to fetch categories/brands for supplier:', err);
                }
            };
            fetchOptions();
        }
    }, [type]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError('');

            let apiUrl = '';
            let cleanedData = {};

            if (type === 'category') {
                apiUrl = `${process.env.REACT_APP_API_URL}/api/foproducts/categories`;
                cleanedData = {
                    category: formData.category.trim(),
                    subcategory: formData.subcategory.filter(sub => sub && sub.trim() !== '')
                };
            } else if (type === 'brand') {
                apiUrl = `${process.env.REACT_APP_API_URL}/api/foproducts/brands`;
                cleanedData = {
                    Brand: formData.Brand.trim()
                };
            } else if (type === 'supplier') {
                apiUrl = `${process.env.REACT_APP_API_URL}/api/foproducts/suppliers`;
                cleanedData = {
                    supplierCode: formData.supplierCode.trim(),
                    supplierName: formData.supplierName ? formData.supplierName.trim() : '',
                    category: Array.isArray(formData.supplierCategory) ? formData.supplierCategory : [],
                    subcategory: Array.isArray(formData.supplierSubcategory) ? formData.supplierSubcategory.filter(s => s && String(s).trim()) : [],
                    brands: Array.isArray(formData.supplierBrands) ? formData.supplierBrands : []
                };
            }

            const token = localStorage.getItem('token');
            const response = await axios.post(
                apiUrl,
                cleanedData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );

            if (response.data.success) {
                onSuccess(response.data.data);
            }
        } catch (err) {
            setError(`Failed to create ${type}: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const title = type === 'category' ? 'Create New Category' : type === 'brand' ? 'Create New Brand' : 'Create New Supplier';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{title}</h2>
                    <button
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-700"
                        aria-label="Close modal"
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        {type === 'category' ? (
                            <>
                                {/* Category */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.category}
                                        onChange={(e) => {
                                            setFormData({ ...formData, category: e.target.value, subcategory: [''] });
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., accessories"
                                        list="categories-list"
                                    />
                                    {categoryNames.length > 0 && (
                                        <datalist id="categories-list">
                                            {categoryNames.map((cat, index) => (
                                                <option key={index} value={cat} />
                                            ))}
                                        </datalist>
                                    )}
                                </div>

                                {/* Subcategories */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Subcategories (Optional)
                                    </label>
                                    {formData.subcategory.map((subcat, index) => (
                                        <div key={index} className="flex gap-2 mb-2">
                                            <input
                                                type="text"
                                                value={subcat}
                                                onChange={(e) => {
                                                    const newSubcategories = [...formData.subcategory];
                                                    newSubcategories[index] = e.target.value;
                                                    setFormData({ ...formData, subcategory: newSubcategories });
                                                }}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                                placeholder="e.g., belt"
                                                list={`subcategories-list-${index}`}
                                            />
                                            {subcategories.length > 0 && (
                                                <datalist id={`subcategories-list-${index}`}>
                                                    {subcategories.map((sub, idx) => (
                                                        <option key={idx} value={sub} />
                                                    ))}
                                                </datalist>
                                            )}
                                            {formData.subcategory.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newSubcategories = formData.subcategory.filter((_, i) => i !== index);
                                                        setFormData({ ...formData, subcategory: newSubcategories.length > 0 ? newSubcategories : [''] });
                                                    }}
                                                    className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFormData({ ...formData, subcategory: [...formData.subcategory, ''] });
                                        }}
                                        className="mt-2 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                    >
                                        + Add Subcategory
                                    </button>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Leave empty if this category has no subcategories (e.g., watches)
                                    </p>
                                </div>
                            </>
                        ) : type === 'brand' ? (
                            /* Brand */
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Brand *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.Brand}
                                    onChange={(e) => setFormData({ ...formData, Brand: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="Brand name"
                                />
                            </div>
                        ) : (
                            /* Supplier */
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Supplier Code *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.supplierCode}
                                        onChange={(e) => setFormData({ ...formData, supplierCode: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., SUP001"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Supplier Name (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.supplierName}
                                        onChange={(e) => setFormData({ ...formData, supplierName: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Supplier name (usually empty)"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Categories (optional, multiple)
                                    </label>
                                    <select
                                        multiple
                                        value={formData.supplierCategory || []}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            supplierCategory: Array.from(e.target.selectedOptions, o => o.value)
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[80px]"
                                    >
                                        {categoriesForSupplier.map((c) => (
                                            <option key={c._id} value={c._id}>{c.category}</option>
                                        ))}
                                    </select>
                                    <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Subcategories (optional, multiple)
                                    </label>
                                    <select
                                        multiple
                                        value={formData.supplierSubcategory || []}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            supplierSubcategory: Array.from(e.target.selectedOptions, o => o.value)
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[80px]"
                                    >
                                        {allSubcategoriesForSupplier.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                    <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Brands (optional, multiple)
                                    </label>
                                    <select
                                        multiple
                                        value={formData.supplierBrands || []}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            supplierBrands: Array.from(e.target.selectedOptions, o => o.value)
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[80px]"
                                    >
                                        {brandsForSupplier.map((b) => (
                                            <option key={b._id} value={b._id}>{b.Brand}</option>
                                        ))}
                                    </select>
                                    <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            disabled={loading}
                        >
                            {loading ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateFOProductForm;
